/**
 * PengiraanTab Component
 * Tab for displaying Faraid calculation results
 * 
 * Reference: FaraidInfo.md - Inheritance distribution display
 */

import { useMemo } from 'react';
import { Card } from '@components/common/Card';
import type { FaraidCalculationResult, ShareResult } from '../../types';
import { formatFraction } from '../../utils';
import styles from './PengiraanTab.module.css';

interface PengiraanTabProps {
  result: FaraidCalculationResult | null;
  totalEstate: number;
  isCalculating: boolean;
  onRecalculate: () => void;
}

/**
 * Empty state component
 */
const EmptyState = () => (
  <div className={styles.emptyState}>
    <p className={styles.emptyText}>
      Sila pilih waris dan masukkan nilai harta pusaka untuk melihat pengiraan.
    </p>
  </div>
);

/**
 * Error state component
 */
const ErrorState = ({ errors }: { errors: string[] }) => (
  <div className={styles.errorState}>
    <h4 className={styles.errorTitle}>Ralat Pengiraan</h4>
    <ul className={styles.errorList}>
      {errors.map((error, index) => (
        <li key={index}>{error}</li>
      ))}
    </ul>
  </div>
);

/**
 * PengiraanTab main component
 */
export const PengiraanTab = ({
  result,
  totalEstate: _totalEstate,
  isCalculating: _isCalculating,
  onRecalculate: _onRecalculate,
}: PengiraanTabProps) => {
  // Filter shares to display (exclude blocked heirs with 0 value for cleaner display)
  const displayShares = useMemo(() => {
    if (!result?.shares) return [];
    return result.shares.filter(share => !share.isBlocked || share.count > 0);
  }, [result?.shares]);

  // Expand shares with count > 1 into individual rows for display
  // Each heir gets their own row with individual amount
  interface ExpandedShare extends ShareResult {
    displayIndex: number;
    individualAmount: number;
    displayLabel: string;
  }
  
  const expandedShares = useMemo((): ExpandedShare[] => {
    if (!result?.shares) return [];
    
    const expanded: ExpandedShare[] = [];
    
    for (const share of result.shares) {
      if (share.isBlocked) {
        // Blocked heirs show as single entry
        expanded.push({
          ...share,
          displayIndex: 1,
          individualAmount: 0,
          displayLabel: share.heirLabel,
        });
      } else if (share.count > 1) {
        // Split into individual rows
        for (let i = 0; i < share.count; i++) {
          expanded.push({
            ...share,
            displayIndex: i + 1,
            individualAmount: share.amountPerHeir,
            displayLabel: `${share.heirLabel} ${i + 1}`,
          });
        }
      } else {
        // Single heir - display as-is
        expanded.push({
          ...share,
          displayIndex: 1,
          individualAmount: share.totalAmount,
          displayLabel: share.heirLabel,
        });
      }
    }
    
    return expanded;
  }, [result?.shares]);

  const activeExpandedShares = useMemo(
    () => expandedShares.filter(s => !s.isBlocked),
    [expandedShares]
  );

  const grandTotal = useMemo(
    () => activeExpandedShares.reduce((sum, share) => sum + share.individualAmount, 0),
    [activeExpandedShares]
  );
  
  // Show empty state if no result
  if (!result) {
    return (
      <div className={styles.container}>
        <EmptyState />
      </div>
    );
  }
  
  // Show error state if validation failed
  if (!result.isValid && result.errors.length > 0) {
    return (
      <div className={styles.container}>
        <ErrorState errors={result.errors} />
      </div>
    );
  }
  
  const isBaitulmalOnly = activeExpandedShares.length === 0 && result.remainderToBaitulmal > 0;

  return (
    <div className={styles.container}>
      {/* Summary Table - Asal Masalah */}
      <Card className={styles.card}>
        <table className={styles.summaryTable}>
          <thead>
            <tr className={styles.tableHeader}>
              <th className={styles.headerCell}>Waris</th>
              <th className={styles.headerCell}>Bahagian</th>
              <th className={styles.headerCell}>{result.baseDenominator}</th>
            </tr>
          </thead>
          <tbody>
            {activeExpandedShares.map((share) => (
              <tr key={`${share.heirType}-${share.displayIndex}`} className={styles.summaryRow}>
                <td className={styles.warisCell}>
                  <span className={styles.warisName}>{share.displayLabel}</span>
                </td>
                <td className={styles.bahagianCell}>
                  {share.fixedShare === '1/3_baki' ? '1/3 Baki' : (share.fixedShare || 'Asobah')}
                </td>
                <td className={styles.fractionCell}>
                  {share.count > 1
                    ? formatFraction(share.fractionNumerator, share.fractionDenominator * share.count)
                    : formatFraction(share.fractionNumerator, share.fractionDenominator)}
                </td>
              </tr>
            ))}
            {isBaitulmalOnly && (
              <tr className={styles.summaryRow}>
                <td className={styles.warisCell}>
                  <span className={styles.warisName}>BAITULMAL</span>
                </td>
                <td className={styles.bahagianCell}>Baki</td>
                <td className={styles.fractionCell}>1</td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
      
      {/* Detail Table - With Amounts */}
      <Card className={styles.card}>
        <table className={styles.detailTable}>
          <thead>
            <tr className={styles.tableHeader}>
              <th className={styles.headerCell}>Waris</th>
              <th className={styles.headerCell}>Bahagian</th>
              <th className={styles.headerCell}>
                {formatFraction(result.finalDenominator, result.finalDenominator)}
              </th>
              <th className={styles.headerCell}>Jumlah (RM)</th>
            </tr>
          </thead>
          <tbody>
            {activeExpandedShares.map((share) => (
              <tr key={`${share.heirType}-${share.displayIndex}`} className={styles.detailRow}>
                <td className={styles.warisCell}>
                  <span className={styles.warisName}>{share.displayLabel}</span>
                </td>
                <td className={styles.bahagianCell}>
                  {share.fixedShare === '1/3_baki' ? '1/3 Baki' : (share.fixedShare || 'Asobah')}
                </td>
                <td className={styles.fractionCell}>
                  {share.count > 1
                    ? formatFraction(share.fractionNumerator, share.fractionDenominator * share.count)
                    : formatFraction(share.fractionNumerator, share.fractionDenominator)}
                </td>
                <td className={styles.amountCell}>
                  {share.individualAmount.toLocaleString('ms-MY', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })}
                </td>
              </tr>
            ))}
            {isBaitulmalOnly && (
              <tr className={styles.detailRow}>
                <td className={styles.warisCell}>
                  <span className={styles.warisName}>BAITULMAL</span>
                </td>
                <td className={styles.bahagianCell}>Baki</td>
                <td className={styles.fractionCell}>1/1</td>
                <td className={styles.amountCell}>
                  {result.remainderToBaitulmal.toLocaleString('ms-MY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
              </tr>
            )}
            <tr className={styles.grandTotalRow}>
              <td className={styles.warisCell} colSpan={3}>
                <span className={styles.grandTotalLabel}>JUMLAH KESELURUHAN</span>
              </td>
              <td className={styles.amountCell}>
                {(isBaitulmalOnly ? result.remainderToBaitulmal : grandTotal).toLocaleString('ms-MY', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </td>
            </tr>
          </tbody>
        </table>
      </Card>
      
      {/* Warnings */}
      {result.warnings.length > 0 && (
        <div className={styles.warningsSection}>
          <h4 className={styles.warningsTitle}>Nota:</h4>
          <ul className={styles.warningsList}>
            {result.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Blocked Heirs (if any) */}
      {displayShares.some(s => s.isBlocked) && (
        <Card className={styles.blockedCard}>
          <h4 className={styles.blockedTitle}>Waris Terdinding (Mahjub)</h4>
          <div className={styles.blockedList}>
            {displayShares
              .filter(s => s.isBlocked)
              .map((share) => (
                <div key={share.heirType} className={styles.blockedItem}>
                  <span className={styles.blockedName}>{share.heirLabel}</span>
                  {share.blockedByHeirs && share.blockedByHeirs.length > 0 && (
                    <span className={styles.blockedReason}>
                      Terdinding oleh waris yang lebih dekat
                    </span>
                  )}
                </div>
              ))}
          </div>
        </Card>
      )}
      
      {/* Baitulmal remainder — only show the footer note when it is a partial remainder, not the sole recipient */}
      {result.remainderToBaitulmal > 0 && !isBaitulmalOnly && (
        <div className={styles.baitulmalSection}>
          <span className={styles.baitulmalLabel}>Baki kepada Baitulmal:</span>
          <span className={styles.baitulmalAmount}>
            RM {result.remainderToBaitulmal.toLocaleString('ms-MY', { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </span>
        </div>
      )}
      
      {/* Special Cases Legend */}
      {(result.specialCases.isAul || result.specialCases.isUmariyyatain) && (
        <Card className={styles.legendCard}>
          <h4 className={styles.legendTitle}>Kes Khas</h4>
          <div className={styles.legendContent}>
            {result.specialCases.isAul && (
              <p>
                <strong>'Aul:</strong> Jumlah bahagian fardu melebihi harta. 
                Asal masalah diselaraskan dari {result.specialCases.aulOriginalDenominator} 
                kepada {result.specialCases.aulNewDenominator}.
              </p>
            )}
            {result.specialCases.isUmariyyatain && (
              <p>
                <strong>'Umariyyatain/Gharrawain:</strong> Ibu mendapat 1/3 daripada baki 
                setelah bahagian suami/isteri.
              </p>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};
